import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DynamicTableComponent } from '../dynamic-table/dynamic-table.component';
import { PROBLEM_RECORDS_CASE_RECORDS_TABLE_CONFIG, PROBLEM_RECORDS_CASE_RECORDS_TABLE_STATE } from './problem-records-case-records-table.config';
import { Subject, take, takeUntil } from 'rxjs';
import { DataTableActionEnum, DataTableStateModel } from '../dynamic-table/dynamic-table.model';
import { ProblemRecordsFormService } from 'app/problem-records-form/problem-records-form.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AddRecordCaseModalComponent } from '../add-record-case-modal/add-record-case-modal.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { HelperService } from 'app/shared/services/helper.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { ToastTypesEnum } from 'app/shared/enums/toast.enum';

@Component({
  selector: 'app-problem-records-case-records',
  standalone: true,
  imports: [
    CommonModule,
    DynamicTableComponent,
    TranslateModule,
  ],
  templateUrl: './problem-records-case-records.component.html',
  styleUrl: './problem-records-case-records.component.scss'
})
export class ProblemRecordsCaseRecordsComponent {
  @Input() problemRecordId!: number;
  @Input() productSuite!: string;
  @Input() productCompany!: string;
  @Input() disabled!: boolean;
  @Output() newCaseRecordsEmitter = new EventEmitter<Object>();

  columns = PROBLEM_RECORDS_CASE_RECORDS_TABLE_CONFIG;
  caseRecords: any[] = [];
  caseRecordsDataTableState: DataTableStateModel = { ...PROBLEM_RECORDS_CASE_RECORDS_TABLE_STATE };
  totalCount: number = 0;
  hasMoreItems: boolean = true;

  private unsubscribe$ = new Subject<void>();

  constructor(
    private problemRecordsFormService: ProblemRecordsFormService,
    private modalService: NgbModal,
    private helperService: HelperService,
    private translate: TranslateService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {

    this.problemRecordsFormService.cases = [];

    if (this.problemRecordId) {
      this.getCaseRecords();
    } else {
      this.resetCaseRecords();
    }
  }

  resetCaseRecords() {
    this.problemRecordsFormService.resetProblemRecordCasesEmiter
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((value: boolean) => {
        if (value) {
          this.caseRecords = [];
        }
      });
  }

  getCaseRecords() {
    this.caseRecords = [];
    this.problemRecordsFormService.getCaseRecords(this.problemRecordId, this.caseRecordsDataTableState)
      .pipe(take(1))
      .subscribe((res: any) => {
        this.caseRecords = res.items;
        this.totalCount = res.totalCount;

      });
  }

  getDataTableState(data: any) {
    this.caseRecordsDataTableState = data;
    this.getCaseRecords();
  }

  getCases(params?: any, scroll?: boolean) {

    if (!this.productSuite || !this.productCompany) {

      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });

      const errorMessage = this.translate.instant('messages.product_suite_and_product_company_must_have_values');

      this.toastr.show(errorMessage, ToastTypesEnum.ERROR);

      return;
    }

    if(!scroll) {
      this.problemRecordsFormService.cases = [];
    }

    params = {
      productSuite: this.productSuite,
      productCompany: this.productCompany,
      filter: params ? params.term : '',
      skipCount: params ? params.page * params.take : 0,
      takeCount: params ? params.take : 50
    }

    this.problemRecordsFormService.getProblemRecordsCaseDropdown(params)
      .pipe(take(1))
      .subscribe((res: Array<{ id: number; caseIdentifier: string; caseTitle: string; customerRegion: string; customerSite: string }>) => {
        if (res.length !== 0) {
          const filtered = res
            .filter((item: { id: number }) => !this.caseRecords.some((record: { id: number }) => record.id === item.id))
            .map((item: { id: number; caseIdentifier: string; caseTitle: string; customerRegion: string; customerSite: string }) => ({
              name: `${item.caseIdentifier} (${item.caseTitle})`,
              id: item.id,
              caseTitle: item.caseTitle,
              caseIdentifier: item.caseIdentifier,
              customerRegion: item.customerRegion,
              customerSite: item.customerSite
            }));

          this.hasMoreItems = res.length === params.takeCount;

          this.problemRecordsFormService.cases = scroll ? [...this.problemRecordsFormService.cases, ...filtered] : filtered;

        }
      });
  }

  openAddRecordCaseModal() {
    const modalRef = this.modalService.open(AddRecordCaseModalComponent, {
      backdrop: 'static',
      keyboard: false,
      windowClass: 'confirmation-modal'
    });

    modalRef.componentInstance.title = 'label.add_case';
    modalRef.componentInstance.notConfirmActionLabel = 'buttons.discard';
    modalRef.componentInstance.confirmActionLabel = 'buttons.save_changes';

    modalRef.componentInstance.onScrollEvent.pipe(takeUntil(this.unsubscribe$)).subscribe((res: any) => {
      if (this.hasMoreItems) {
        this.getCases(res, true);
      }
    });

    modalRef.componentInstance.onSearchEvent.pipe(takeUntil(this.unsubscribe$)).subscribe((res: any) => {
      this.hasMoreItems = true;
      this.getCases(res);
    });

    modalRef.componentInstance.onClearEvent.pipe(takeUntil(this.unsubscribe$)).subscribe((res: any) => {
      this.problemRecordsFormService.cases = [];
    });

    modalRef.componentInstance.onOpenEvent.pipe(takeUntil(this.unsubscribe$)).subscribe((res: any) => {
      this.hasMoreItems = true;
      this.getCases();
    });

    modalRef.result.then(
      (result: any) => {
        if (result && result.action) {
          if (this.problemRecordId) {

            this.addOrRemoveProblemRecordsCase(result.value.id, false);
          } else {

            let newRow = {};
            newRow = this.helperService.deepCopy(result.value);

            if (newRow) {
              this.caseRecords.unshift(newRow);
              this.caseRecords = [...this.caseRecords];
              this.newCaseRecordsEmitter.emit(this.caseRecords)
            }
          }

        }
      },
      (reason: any) => { }
    );
  }

  addOrRemoveProblemRecordsCase(caseId: number, isDelete: boolean) {
    this.problemRecordsFormService.addOrRemoveProblemRecordsCase(this.problemRecordId, caseId, isDelete)
      .pipe(take(1))
      .subscribe((res: any) => {
        this.getCaseRecords();
      });
  }

  onActionButton(data: any) {
    if (data.action === DataTableActionEnum.DELETE) {
      const modalRef = this.modalService.open(ConfirmationDialogComponent, {
        backdrop: 'static',
        keyboard: false,
        windowClass: 'confirmation-modal'
      });

      modalRef.componentInstance.title = 'buttons.delete';
      modalRef.componentInstance.message = 'messages.are_you_sure_you_want_to_delete_this_item';
      modalRef.componentInstance.notConfirmActionLabel = 'label.cancel';
      modalRef.componentInstance.confirmActionLabel = 'label.confirm';

      modalRef.result.then(
        (result) => {
          if (result) {
            if (this.problemRecordId && data.row.id) {

              this.addOrRemoveProblemRecordsCase(data.row.id, true);
            } else {

              this.caseRecords.splice(data.rowIndex, 1);
              this.caseRecords = [...this.caseRecords];
            }
          }
        }
      );
    }
  }

  getLinkedValue(data: any) {
    const url = `case-form/${data.row.id}`;

    window.open(url, '_blank');
  }

}
